/* WeQuote Dev Board — Completed/Live + the announce workflow (PRD §5.8,
 * verify §59). Announce/Publish/WhatsApp are DATE-STAMP TOGGLES at parity —
 * the real requester emails, Updates-page publish and WhatsApp wiring are the
 * agreed post-parity integrations; the CONTRACTS (announced = announce +
 * publish; handled also counts dismissed) are what these functions pin down. */
import type { Feature } from './types'
import { insertByScore } from './suggest'
import { todayISO } from './dates'
import { reqCount } from './logic'
import { calWorkingDaysBetween } from './calendar'
import type { MutResult } from './mutations'

export type LiveScope = 'month' | 'year' | 'all'
export function liveInScope(f: Feature, scope: LiveScope, now?: Date): boolean {
  if (f.status !== 'live' || !f.completedOn) return false
  if (scope === 'all') return true
  const p = ('' + f.completedOn).split('-')
  const n = now || new Date()
  if (+p[0] !== n.getFullYear()) return false
  if (scope === 'year') return true
  return +p[1] === n.getMonth() + 1
}
export function liveAnnounced(f: Feature): boolean { return !!(f.announcedOn && f.publishedOn) }
export function liveHandled(f: Feature): boolean { return liveAnnounced(f) || !!f.noAnnounce }
export function actualDevDays(f: Feature): number | null {
  return f.start && f.completedOn ? calWorkingDaysBetween(f.start, f.completedOn, f.dev) : null
}

const flip = (fs: Feature[], id: number, fn: (f: Feature) => Feature): Feature[] =>
  fs.map((f) => (f.id === id ? fn({ ...f }) : f))

export function announceFeature(fs: Feature[], id: number): MutResult {
  const f = fs.find((x) => x.id === id)
  if (!f) return { features: fs }
  if (f.announcedOn) {
    return { features: flip(fs, id, (g) => ({ ...g, announcedOn: '' })), toast: 'Announcement undone for “' + f.title + '”.', changedIds: [id] }
  }
  const n = reqCount(f)
  return {
    features: flip(fs, id, (g) => ({ ...g, announcedOn: todayISO() })),
    toast: n ? 'Announced “' + f.title + '” to ' + n + ' compan' + (n === 1 ? 'y' : 'ies') + ' · emails simulated.' : 'Marked “' + f.title + '” announced (no requesters on file).',
    changedIds: [id],
  }
}
export function publishFeature(fs: Feature[], id: number): MutResult {
  const f = fs.find((x) => x.id === id)
  if (!f) return { features: fs }
  const on = !f.publishedOn
  return {
    features: flip(fs, id, (g) => ({ ...g, publishedOn: on ? todayISO() : '' })),
    toast: on ? '“' + f.title + '” published to the updates page.' : 'Unpublished “' + f.title + '”.',
    changedIds: [id],
  }
}
export function whatsappAnnounce(fs: Feature[], id: number): MutResult {
  const f = fs.find((x) => x.id === id)
  if (!f) return { features: fs }
  const on = !f.whatsappOn
  return {
    features: flip(fs, id, (g) => ({ ...g, whatsappOn: on ? todayISO() : '' })),
    toast: on ? '“' + f.title + '” announced in WhatsApp support groups · wiring pending.' : 'WhatsApp announcement undone for “' + f.title + '”.',
    changedIds: [id],
  }
}
export function dismissAnnounce(fs: Feature[], id: number): MutResult {
  const f = fs.find((x) => x.id === id)
  if (!f) return { features: fs }
  const on = !f.noAnnounce
  return {
    features: flip(fs, id, (g) => ({ ...g, noAnnounce: on })),
    toast: on ? '“' + f.title + '” marked as no announcement needed.' : 'Re-opened “' + f.title + '” for announcement.',
    changedIds: [id],
  }
}

/* ── Ideas (verify §63) ── */
export function promoteIdea(fs: Feature[], id: number): MutResult {
  let title = ''
  const next = fs.map((f) => {
    if (f.id !== id) return f
    title = f.title
    return { ...f, status: 'later', onhold: false } // v2 §1: promoted ideas land in Later
  })
  const scored = insertByScore(next, id)
  return { features: scored, toast: '“' + title + '” moved to the priority list (Later).', changedIds: [id] }
}
